import { type ReactNode } from 'react'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '#app/components/ui/sheet.tsx'
import { useIsMobile } from '#app/hooks/use-mobile'

type Side = 'top' | 'right' | 'bottom' | 'left'

export interface ResponsiveOverlayProps {
	open?: boolean
	onOpenChange?: (open: boolean) => void
	title?: ReactNode
	description?: ReactNode
	trigger?: ReactNode
	children?: ReactNode
	footer?: ReactNode
	side?: Side
	className?: string
}

export function ResponsiveOverlay({
	open,
	onOpenChange,
	title,
	description,
	trigger,
	children,
	footer,
	side = 'bottom',
	className,
}: ResponsiveOverlayProps) {
	const isMobile = useIsMobile()

	if (isMobile) {
		return (
			<Sheet
				key="mobile"
				{...(open === undefined ? {} : { open, onOpenChange })}
			>
				{trigger ? <SheetTrigger asChild>{trigger}</SheetTrigger> : null}
				<SheetContent side={side} className={className}>
					{title || description ? (
						<SheetHeader>
							{title ? <SheetTitle>{title}</SheetTitle> : null}
							{description ? (
								<SheetDescription>{description}</SheetDescription>
							) : null}
						</SheetHeader>
					) : null}
					{children}
					{footer ? <SheetFooter>{footer}</SheetFooter> : null}
				</SheetContent>
			</Sheet>
		)
	}

	return (
		<Dialog
			key="desktop"
			{...(open === undefined ? {} : { open, onOpenChange })}
		>
			{trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
			<DialogContent className={className}>
				{title || description ? (
					<DialogHeader>
						{title ? <DialogTitle>{title}</DialogTitle> : null}
						{description ? (
							<DialogDescription>{description}</DialogDescription>
						) : null}
					</DialogHeader>
				) : null}
				{children}
				{footer ? <DialogFooter>{footer}</DialogFooter> : null}
			</DialogContent>
		</Dialog>
	)
}

export function ResponsiveOverlayClose({ children }: { children: ReactNode }) {
	const isMobile = useIsMobile()
	if (isMobile) {
		return <SheetClose asChild>{children}</SheetClose>
	}
	return <DialogClose asChild>{children}</DialogClose>
}
